import { EasyTab, EasyTabContent, EasyTabItem } from '@ui/components/moleculas';
import { IConversationSet } from '@ui/components/organisms/conversation';
import { useMessageContext } from '@ui/components/organisms/message';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { ContextUserType } from '@ui/constants/context';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';
import { twMerge } from 'tailwind-merge';
import { PlusIcon } from 'lucide-react';
import { Button } from '@ui/components/ui/button';
import { useRouter } from 'next/navigation';
import { usePostHogFeatureFlag } from '@ui/hooks';
import PostListSegment from './post-list-segment';
interface IProps {
  emptyState: {
    title: string;
    message: string;
  };
  userType: ContextUserType;
  isLoadingCommunityConversations: boolean;
  isCommunityConversationSuccess: boolean;
  communitySets: IConversationSet[];
  isDynamicRoute?: boolean;
  isAuthenticated?: boolean;
}

export const PostList = ({
  emptyState,
  userType,
  isLoadingCommunityConversations,
  isCommunityConversationSuccess,
  communitySets,
  isDynamicRoute,
  isAuthenticated,
}: IProps) => {
  const messageState = useMessageContext(userType);
  const [tabs, setTabs] = useState<string[]>([]);
  const isFanEnabledToCreatPost = usePostHogFeatureFlag('enable-fan-to-create-post');
  const router = useRouter();
  const [initialLoading, setInitialLoading] = useState(true);
  const isLoading = useMemo(
    () => isLoadingCommunityConversations || messageState.isLoading,
    [isLoadingCommunityConversations, messageState.isLoading],
  );

  const showEmptyScreen = useMemo(() => {
    const hasCommunity = isCommunityConversationSuccess && communitySets.length > 0;
    const hasMessages = messageState.isSuccess && messageState.messages.length === 0;

    return hasCommunity && hasMessages && !isLoading;
  }, [
    isCommunityConversationSuccess,
    communitySets.length,
    messageState.isSuccess,
    messageState.messages.length,
    isLoading,
  ]);

  useEffect(() => {
    if (isCommunityConversationSuccess && messageState.isSuccess) {
      setInitialLoading(false);
    }
  }, [isCommunityConversationSuccess, messageState.isSuccess]);

  const nextPage = useCallback(() => {
    messageState.onNextPage();
  }, [messageState]);

  const handleCreatePost = useCallback(() => {
    let path = '/dashboard/community';
    if (userType === 'fan') {
      const community = communitySets[0]?.conversation;
      path = `/creator/community/create-post?creatorId=${community.creator_id}&communityId=${community.id}`;
    }
    router.push(path);
  }, [communitySets, userType]);

  const onTabChanged = (tab: string) => {
    if (tab === 'crew') {
      messageState.onSetCreatedByCreator(false);
      messageState.onSetPage(1);
    } else {
      messageState.onSetCreatedByCreator(true);
      messageState.onSetPage(1);
    }
  };

  useEffect(() => {
    if (messageState?.community_details?.creator_display_name) {
      setTabs([messageState.community_details.creator_display_name, 'crew']);
      messageState.onSetCreatedByCreator(true);
    }
  }, [
    messageState?.community_details?.creator_display_name,
    messageState?.community_details?.creator_id,
  ]);

  return (
    <MessageUpdateFormProvider>
      <>
        {messageState.messages.length > 0 && (
          <MessageUpdateFormPortal userType={userType} main_post_ref="" />
        )}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p
                className={twMerge(
                  'font-nunito text-lg font-semibold',
                  userType === 'fan' ? 'px-4 lg:px-0' : '',
                )}>
                Posts
              </p>
              {/* <Link
                href={
                  userType === 'creator'
                    ? '/dashboard/community-guidelines'
                    : '/community-guidelines'
                }
                className={twMerge(
                  'text-xs underline hover:text-primary',
                  userType === 'fan' ? 'px-4 lg:px-0' : '',
                )}>
                Community Guidelines
              </Link> */}
            </div>
            <div>
              {userType === 'fan' && isFanEnabledToCreatPost && isAuthenticated && (
                <Button variant="outline" size="sm" onClick={handleCreatePost}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Post
                </Button>
              )}
            </div>
          </div>
          {messageState.community_details?.creator_display_name && tabs.length > 0 && (
            <EasyTab
              defaultTab={tabs[0]}
              className="min-h-[200px] overflow-hidden space-y-4"
              onTabChanged={onTabChanged}>
              {isFanEnabledToCreatPost && (
                <div className="flex justify-center w-full">
                  <div className="flex gap-2 mx-auto border border-gray-300 rounded-full w-full p-1 bg-gray-50">
                    {tabs.map((tab) => (
                      <EasyTabItem
                        key={tab}
                        label={tab}
                        useFullBorderWhenActive={false}
                        useBottomActiveBorder={false}
                        className="min-w-20 px-4 py-2"
                        whenActiveClassName="bg-primary !text-white rounded-full"
                        whenActiveTextClassName="text-white"
                        textClassName="text-xs"
                      />
                    ))}
                  </div>
                </div>
              )}

              <EasyTabContent label={tabs[0]}>
                <PostListSegment
                  messageState={messageState}
                  isLoading={isLoading}
                  initialLoading={initialLoading}
                  emptyState={emptyState}
                  showEmptyScreen={showEmptyScreen}
                  nextPage={nextPage}
                  userType={userType}
                  isDynamicRoute={isDynamicRoute}
                />
              </EasyTabContent>

              <EasyTabContent label={tabs[1]}>
                <PostListSegment
                  messageState={messageState}
                  isLoading={isLoading}
                  initialLoading={initialLoading}
                  emptyState={emptyState}
                  showEmptyScreen={showEmptyScreen}
                  nextPage={nextPage}
                  userType={userType}
                  isDynamicRoute={isDynamicRoute}
                />
              </EasyTabContent>
            </EasyTab>
          )}
        </div>
      </>
    </MessageUpdateFormProvider>
  );
};
